import { cn } from "@/utils";
import { File, FileChartColumn, FileCheck } from "lucide-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import {
  isFileTypeAllowed,
  isOsFileDrag,
  readFilesFromDataTransfer,
} from "./osFileDrop";

type DropCallback = (files: File[]) => void;

interface OsFileDropContextType {
  enabled: boolean;
  registerDropCallback: (callback: DropCallback) => void;
  unregisterDropCallback: (callback: DropCallback) => void;
}

const OsFileDropContext = createContext<OsFileDropContextType | null>(null);

function OsFileDropOverlay({
  active,
  className,
  style,
}: {
  active: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (!active) return null;
  return (
    <div
      className={cn(
        "absolute top-0 left-0 right-0 bottom-0 h-full w-full z-10 inset-0",
        "bg-background/60 backdrop-blur-xs border-2 border-dashed border-neutral-300",
        "flex items-center justify-center pointer-events-none",
        className,
      )}
      style={style}
    >
      <div className="flex flex-col items-center gap-4 relative top-[-30px]">
        <div className="relative w-24 h-24">
          <FileCheck className="stroke-1 fill-white stroke-green-500 dark:fill-black absolute w-[50px] h-[50px] text-primary rotate-[-15deg] translate-x-[-25px] translate-y-[25px]" />
          <File className="stroke-1 fill-white stroke-green-500 dark:fill-black absolute w-[75px] h-[75px] text-primary translate-y-[8px]" />
          <FileChartColumn className="stroke-1 fill-white stroke-green-500 dark:fill-black absolute w-[100px] h-[100px] text-primary rotate-[20deg] translate-x-[40px]" />
        </div>
        <div className="relative top-[20px] text-lg text-primary text-center text-balance px-4">
          Drop files here to add them to the conversation
        </div>
      </div>
    </div>
  );
}

export function OsFileDropLayer({
  children,
  className,
  style,
  enabled: enabledProp,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  enabled?: boolean;
}) {
  const dropCallbacksRef = useRef(new Set<DropCallback>());
  const [callbackCount, setCallbackCount] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const enabled = useMemo(
    () => callbackCount > 0 || Boolean(enabledProp),
    [callbackCount, enabledProp],
  );

  const registerDropCallback = useCallback((callback: DropCallback) => {
    dropCallbacksRef.current.add(callback);
    setCallbackCount(dropCallbacksRef.current.size);
  }, []);

  const unregisterDropCallback = useCallback((callback: DropCallback) => {
    dropCallbacksRef.current.delete(callback);
    setCallbackCount(dropCallbacksRef.current.size);
  }, []);

  const dispatchFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;
    dropCallbacksRef.current.forEach((callback) => callback(files));
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      if (!enabled || !isOsFileDrag(event.nativeEvent)) return;
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      const files = await readFilesFromDataTransfer(event.dataTransfer);
      const rejected = Array.from(event.dataTransfer.files).filter(
        (f) => !isFileTypeAllowed(f),
      );
      for (const file of rejected) {
        toast.error(`Unsupported file type: ${file.name}`);
      }
      dispatchFiles(files);
    },
    [dispatchFiles, enabled],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!enabled || !isOsFileDrag(event.nativeEvent)) return;
      event.preventDefault();
    },
    [enabled],
  );

  // Overlay activation tracks the pointer via document-capture listeners
  // instead of enter/leave depth counting: Chrome reports `relatedTarget:
  // null` for native OS drags, which broke the descendant guards and left
  // the counter drifting (overlay active while the drag was over the left
  // rail, and stuck after a drop the rail swallowed with stopPropagation —
  // capture-phase listeners fire regardless). The overlay is active exactly
  // while the drag is over THIS layer's subtree, so the rail's own precise
  // drop zones never show the page overlay next to their highlight.
  useEffect(() => {
    if (!enabled) {
      setDragActive(false);
      return;
    }

    const onDragOver = (e: DragEvent) => {
      if (!isOsFileDrag(e)) return;
      const root = rootRef.current;
      setDragActive(
        !!root && e.target instanceof Node && root.contains(e.target),
      );
    };
    const clear = () => setDragActive(false);
    const onDragLeave = (e: DragEvent) => {
      // Leaving the window: native drags report no relatedTarget and edge /
      // out-of-viewport coordinates.
      if (e.relatedTarget) return;
      if (
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        clear();
      }
    };

    document.addEventListener("dragover", onDragOver, true);
    document.addEventListener("drop", clear, true);
    document.addEventListener("dragend", clear, true);
    document.addEventListener("dragleave", onDragLeave, true);
    return () => {
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("drop", clear, true);
      document.removeEventListener("dragend", clear, true);
      document.removeEventListener("dragleave", onDragLeave, true);
    };
  }, [enabled]);

  const contextValue = useMemo(
    () => ({
      enabled: Boolean(enabled),
      registerDropCallback,
      unregisterDropCallback,
    }),
    [enabled, registerDropCallback, unregisterDropCallback],
  );

  return (
    <OsFileDropContext.Provider value={contextValue}>
      <div
        ref={rootRef}
        className={cn("h-full w-full flex flex-col relative", className)}
        id="dropzone"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <OsFileDropOverlay active={dragActive} style={style} />
        {children}
      </div>
    </OsFileDropContext.Provider>
  );
}

export function useRegisterOsFileDrop(callback?: DropCallback) {
  const context = useContext(OsFileDropContext);

  useEffect(() => {
    if (!context || !callback) return;
    context.registerDropCallback(callback);
    return () => context.unregisterDropCallback(callback);
  }, [callback, context]);
}

/** @deprecated Use `useRegisterOsFileDrop`. */
export const useOnDrop = useRegisterOsFileDrop;

/** @deprecated Use `OsFileDropLayer`. */
export const DropZoneProvider = OsFileDropLayer;
